/**
 * 音频管理器
 * 负责管理音频列表，提供获取、切换收藏等功能
 * @author 杨俊朗
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { canUseWindow } from "@/utils/canuseWindow";
import { creationAudioFilterOptionsManager } from "@/manager";
import { useToast } from "@/hooks/use-toast";
import { audioShow } from "@/services/audio-show";
import { audioToggleFavorite } from "@/services/audio-toogle-favorite";
import { myAudioToggleFavorite } from "@/services/audio-template-toggle-favorite";
import { voiceRename } from "@/services/audio-rename-template";
import { useMatch } from "react-router-dom";

// 导出常量供外部使用
export const AUDIO_CONDITIONS = {
  ALL: "all_voices" as const,
  FAVORITE: "starred_voices" as const,
  RECENT: "recently_used" as const,
  DEFAULT: "default_voices" as const,
  MY_VOICE: "my_voices" as const,
} as const;

export type AudioChangeEventParams = {
  name: string;
  condition: AudioCondition;
  filters: { [key: string]: string };
  audios: Audio[];
  total: number;
};

export type AudioCondition =
  (typeof AUDIO_CONDITIONS)[keyof typeof AUDIO_CONDITIONS];

// 缓存数据结构
interface CacheData {
  audios: Audio[];
  condition: AudioCondition;
  filters: { [key: string]: string };
  name: string;
  page: number;
  size: number;
  total: number;
  timestamp: number;
}

// 音频管理器
export class CreationAudiosManager {
  private static instance: CreationAudiosManager;
  private readonly STORAGE_KEY = "MIUTA_AUDIOS";
  private audios: Audio[] = [];
  private name: string = "";
  private condition: AudioCondition = AUDIO_CONDITIONS.MY_VOICE; // 默认值改为 my_voices
  private filters: { [key: string]: string } = {};
  private page: number = 1;
  private size: number = 1000;
  private total: number = 0;
  private cache = new Map<string, CacheData>();

  private constructor() { }

  // 单例模式
  public static getInstance(): CreationAudiosManager {
    if (!CreationAudiosManager.instance) {
      CreationAudiosManager.instance = new CreationAudiosManager();
    }
    return CreationAudiosManager.instance;
  }

  // 生成缓存键
  private generateCacheKey(
    condition: AudioCondition,
    filters: { [key: string]: string },
    name: string
  ): string {
    return JSON.stringify({ condition, filters, name });
  }

  // 获取缓存数据
  private getCacheData(
    condition: AudioCondition,
    filters: { [key: string]: string },
    name: string
  ): CacheData | null {
    const key = this.generateCacheKey(condition, filters, name);
    const cached = this.cache.get(key);
    if (!cached) return null;
    return cached;
  }

  // 设置缓存数据
  private setCacheData(
    condition: AudioCondition,
    filters: { [key: string]: string },
    name: string,
    audios: Audio[],
    page: number,
    size: number,
    total: number
  ): void {
    const key = this.generateCacheKey(condition, filters, name);
    const cacheData: CacheData = {
      audios: [...audios],
      condition,
      filters: { ...filters },
      name,
      page,
      size,
      total,
      timestamp: Date.now(),
    };
    this.cache.set(key, cacheData);
  }

  public async fetch(userId?: string): Promise<void> {
    // 先重置需要重置到当前数据
    this.audios = [];
    this.page = 1;
    this.total = 0;
    this.dispatch();
    // 尝试从缓存获取数据
    const cached = this.getCacheData(this.condition, this.filters, this.name);
    // if (cached) {
    //   console.log("使用音频缓存数据", {
    //     condition: this.condition,
    //     filters: this.filters,
    //     name: this.name,
    //   });
    //   this.audios = cached.audios;
    //   this.condition = cached.condition;
    //   this.filters = cached.filters;
    //   this.name = cached.name;
    //   this.page = cached.page;
    //   this.size = cached.size;
    //   this.total = cached.total;
    //   this.dispatch();
    //   return;
    // }

    console.log("从网络获取音频数据", {
      condition: this.condition,
      filters: this.filters,
      name: this.name,
    });
    // 转换选项格式
    const entries = Object.entries(this.filters);
    const options = entries.map(([key, value]) => [key, [value]]);
    const filters = Object.fromEntries(options);
    const data = await audioShow({
      page: this.page,
      size: this.size,
      name: this.name,
      condition: this.condition,
      filters,
      userId,
    });
    this.audios = data.audios;
    this.total = data.total;
    // 缓存数据
    this.setCacheData(
      this.condition,
      this.filters,
      this.name,
      this.audios,
      this.page,
      this.size,
      this.total
    );

    this.dispatch();
  }

  // 获取当前音频列表
  public get(): Audio[] {
    return this.audios;
  }

  public async toggleFavorite(audioId: string): Promise<void> {
    const audio = this.audios.find((audio) => audio.id === audioId);
    if (!audio) {
      throw new Error(`Audio with id ${audioId} not found`);
    }
    // 乐观更新
    const favorite = audio.isStarred;
    this.audios = this.audios.map((item) =>
      item.id === audioId ? { ...item, isStarred: !favorite } : item
    );
    this.dispatch();
    try {
      // 调用接口，my_voice 的音频是另一个接口
      if (this.condition === AUDIO_CONDITIONS.MY_VOICE || audio.source === 'user_template') {
        await myAudioToggleFavorite(audioId);
      } else {
        await audioToggleFavorite(audioId);
      }
      // 成功后清除缓存
      this.clearCache();
    } catch (error) {
      // 回退更新
      this.audios = this.audios.map((item) =>
        item.id === audioId ? { ...item, isStarred: favorite } : item
      );
      this.dispatch();
      // 继续抛出错误
      throw error;
    }
  }

  public async rename({ audio, name }: { audio: Audio, name: string }) {
    const index = this.audios.findIndex(item => item.id === audio.id);
    const originName = this.audios[index].name;
    try {
      // 乐观更新
      this.audios[index].name = name;
      this.audios = [...this.audios];
      this.dispatch();
      const res = await voiceRename({ id: audio.id, name });
      if (res.isDuplicate) {
        // 回退更新
        this.audios[index].name = originName;
        this.audios = [...this.audios];
        this.dispatch();
      }
      // 继续返回结果
      return res;
    } catch (error) {
      // 回退更新
      this.audios[index].name = originName;
      this.audios = [...this.audios];
      this.dispatch();
      // 继续抛出异常
      throw error;
    }
  }

  // name 设置和获取方法
  public setName(name: string): void {
    console.log("setName", name);
    this.name = name;
    this.dispatch();
  }

  public getName(): string {
    return this.name;
  }

  // condition 设置和获取方法
  public setCondition(condition: AudioCondition): void {
    console.log("setCondition", condition);
    this.condition = condition;
    this.dispatch();
  }

  public getCondition(): AudioCondition {
    return this.condition;
  }

  // filters 设置和获取方法
  public setFilters(filters: { [key: string]: string }): void {
    console.log("setFilters", filters);
    this.filters = filters;
    this.dispatch();
  }

  public getFilters(): { [key: string]: string } {
    return this.filters;
  }

  // page 获取方法（内部管理，不提供设置方法）
  public getPage(): number {
    return this.page;
  }

  // size 获取方法（内部管理，不提供设置方法）
  public getSize(): number {
    return this.size;
  }

  // total 获取方法
  public getTotal(): number {
    return this.total;
  }

  // 添加我的语音
  public addMyVoice(voice: Audio): void {
    // 只有我的音频条件下可插入数据
    if (this.condition === AUDIO_CONDITIONS.MY_VOICE) {
      this.audios = [voice, ...this.audios];
      this.dispatch();
    }
  }

  // 重置参数到默认值
  public reset(): void {
    this.name = "";
    this.audios = [];
    this.condition = AUDIO_CONDITIONS.ALL;
    this.filters = {};
    this.page = 1;
    this.size = 1000;
    this.total = 0;
  }

  // 清除所有缓存
  public clearCache(): void {
    this.cache.clear();
    console.log("音频缓存已清除");
  }

  // 监听音频列表变化
  public listen(listener: (e: CustomEvent<AudioChangeEventParams>) => void) {
    if (canUseWindow()) {
      window.addEventListener(this.STORAGE_KEY, listener as EventListener);

      return () => {
        window.removeEventListener(this.STORAGE_KEY, listener as EventListener);
      };
    }
    return () => { };
  }

  // 触发监听回调
  private dispatch(): void {
    if (canUseWindow()) {
      const event = new CustomEvent<AudioChangeEventParams>(this.STORAGE_KEY, {
        detail: {
          name: this.name,
          condition: this.condition,
          filters: this.filters,
          audios: this.audios,
          total: this.total,
        },
      });
      window.dispatchEvent(event);
    }
  }
}

export const useAudios = () => {
  const toast = useToast();
  const [audios, setAudios] = useState<Audio[]>(creationAudiosManager.get());
  const [name, setName] = useState(creationAudiosManager.getName());
  const [condition, setCondition] = useState(creationAudiosManager.getCondition());
  const [filters, setFilters] = useState(creationAudiosManager.getFilters());
  const [total, setTotal] = useState(creationAudiosManager.getTotal());
  const [loading, setLoading] = useState(false);

  // 在agent用户详情页需要传userId
  const _userId = new URLSearchParams(window.location.search).get("userId");
  const isUserDetail = useMatch("/agent/dashboard/user-management/:id/:userId");

  const fetch = async () => {
    try {
      setLoading(true);
      await creationAudiosManager.fetch(isUserDetail ?  _userId: undefined);
    } catch (error) {
      toast.showToastByCode(error);
    } finally {
      setLoading(false);
    }
  };

  // 首次加载
  useEffect(() => {
    fetch();
  }, []);

  // 监听 manager 数据变化
  useEffect(() => {
    const unsubscribe = creationAudiosManager.listen((e) => {
      setAudios(e.detail.audios);
      setName(e.detail.name);
      setCondition(e.detail.condition);
      setFilters(e.detail.filters);
      setTotal(e.detail.total);
    });
    return () => unsubscribe?.();
  }, []);

  // 修改名称
  const changeName = async (newName: string) => {
    if (newName.length > 100) {
      toast.toast({
        title: "Name too long",
        description: "Please input name less than 100 characters",
      });
      return;
    }
    creationAudiosManager.setName(newName);
    await fetch();
  };

  // 修改条件
  const changeCondition = async (newCondition: AudioCondition) => {
    if (!Object.values(AUDIO_CONDITIONS).includes(newCondition)) {
      toast.toast({
        title: "Invalid condition",
        description: "Please select a valid condition",
      });
      return;
    }
    creationAudiosManager.setCondition(newCondition);
    await fetch();
  };

  // 修改筛选
  const changeFilters = async (newFilters: Record<string, string>) => {
    const defaultOption = creationAudioFilterOptionsManager.defaultOption;
    const options = creationAudioFilterOptionsManager.get();
    const validatedFilters: Record<string, string> = {};
    for (const [key, value] of Object.entries(newFilters)) {
      if (options[key] && value !== defaultOption.value) {
        validatedFilters[key] = value;
      }
    }
    for (const [key, value] of Object.entries(validatedFilters)) {
      const valid = options[key].some((option) => option.value === value);
      if (!valid) {
        toast.toast({
          title: "Invalid filter value",
          description: `Please select a valid value for ${key}`,
        });
        return;
      }
    }
    creationAudiosManager.setFilters(validatedFilters);
    await fetch();
  };

  return {
    audios,
    name,
    condition,
    filters,
    total,
    loading,
    changeName,
    changeCondition,
    changeFilters,
  };
};

export const useToggleFavorite = () => {
  const toggleFavorite = async (audioId: string) => {
    await creationAudiosManager.toggleFavorite(audioId);
  };

  return {
    toggleFavorite,
  };
};

export const useAddMyVoice = () => {
  const addMyVoice = (audio: Audio) => {
    creationAudiosManager.addMyVoice(audio);
  };

  return {
    addMyVoice,
  };
};

export const useRename = () => {
  const { t } = useTranslation();
  const { error, showToastByCode } = useToast();

  const rename = async (audio: Audio, name: string) => {
    try {
      const result = await creationAudiosManager.rename({ audio, name });
      // 重名需提示用户
      if (result.isDuplicate) {
        error({ description: t("audios_manager.duplicate_audio_name") });
      }
    } catch (error) {
      showToastByCode(error);
    }
  };

  return {
    rename,
  };
};


export const creationAudiosHooks = {
  useAudios,
  useToggleFavorite,
  useAddMyVoice,
  useRename,
};

// 导出单例实例
export const creationAudiosManager = CreationAudiosManager.getInstance();
